// 多组件库的国际化和本地项目国际化兼容
import type { App, WritableComputedRef } from 'vue'
import { type Composer, type I18n, createI18n } from 'vue-i18n'

// element-plus国际化
import enLocale from 'element-plus/dist/locale/en.mjs'
import zhLocale from 'element-plus/dist/locale/zh-cn.mjs'
import twLocale from 'element-plus/dist/locale/zh-tw.mjs'

// 导入语言配置

function siphonI18n(prefix = 'zh-CN') {
  try {
    // 加载指定语言目录下的所有 JSON 文件
    const files = import.meta.glob('./**/*.json', { eager: true })

    // 过滤出指定语言的文件
    const langFiles = Object.entries(files).filter(([path]) => path.includes(`/${prefix}/`))

    // 合并所有翻译
    return langFiles.reduce((acc, [path, module]: [string, any]) => {
      // 获取模块名 (menus, buttons 等)
      const moduleName = path.split('/').pop()?.replace('.json', '') || ''
      return {
        ...acc,
        [moduleName]: module.default || {},
      }
    }, {})
  } catch (error) {
    console.error(`Failed to load i18n files for ${prefix}:`, error)
    return {}
  }
}

// 语言配置
export const localesConfigs = {
  'zh-CN': {
    ...siphonI18n('zh-CN'),
    ...zhLocale,
  },
  'zh-TW': {
    ...siphonI18n('zh-TW'),
    ...twLocale,
  },
  en: {
    ...siphonI18n('en'),
    ...enLocale,
  },
}

// 初始化语言
export function initlang() {
  // 优先从 localStorage 获取
  const lang = localStorage.getItem('language')
  if (lang && ['zh-CN', 'en', 'zh-TW'].includes(lang)) {
    return lang
  }
  // 获取浏览器语言
  const browserLang = navigator.language
  if (browserLang.includes('zh')) {
    return browserLang.includes('TW') ? 'zh-TW' : 'zh-CN'
  }
  return 'zh-CN'
}

/**
 * 国际化转换工具函数
 * @param message message
 * @returns 转化后的message
 */
export function transformI18n(message: any = '') {
  if (!message) {
    return ''
  }

  // 处理存储动态路由的title,格式 {zh:"",en:""}
  if (typeof message === 'object') {
    const locale: string | WritableComputedRef<string> | any = i18n.global.locale
    return message[locale?.value]
  }

  try {
    // 处理带模块的key，如 'buttons.login'
    const [module, key] = message.split('.')
    const zhMessages = siphonI18n('zh-CN')

    // 检查模块和key是否存在
    if (module && key && zhMessages[module]?.[key]) {
      return i18n.global.t.call(i18n.global.locale, message)
    }
    // 处理直接使用的key
    else if (
      !key &&
      Object.values(zhMessages).some((moduleMessages) =>
        Object.keys(moduleMessages as object).includes(message),
      )
    ) {
      return i18n.global.t.call(i18n.global.locale, message)
    }

    return message
  } catch (error) {
    console.error('Transform i18n error:', error)
    return message
  }
}
// 创建 i18n 实例
export const i18n = createI18n({
  legacy: false,
  locale: initlang(),
  fallbackLocale: initlang(),
  messages: localesConfigs,
  globalInjection: true,
})

// 导出 useI18n 函数
export function useI18n(app: App) {
  app.use(i18n)
}

export function globalT(key: string): string {
  return (i18n.global as Composer).t(key) as string
}

export default i18n
